import { MenuItem, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelectSingletonPoi } from 'features/mapping/hooks/useSelectSingletonPoi';
import { useMeasureDistanceToolState } from 'state/useMeasureDistanceToolState';
import { useMap } from 'shared/map-exports';
import { useLockedMapPin, useLatLngFromUrl } from 'features/mapping/mapPin';
import { Link } from 'react-router-dom';
import { UserStatusChip } from 'components/UserStatusChip';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useAuthState } from 'state/useAuthState';
import { MapToolsMenuItemProps } from './types';

export const MeasureDistanceMenuItem = (
  props: MapToolsMenuItemProps,
): JSX.Element => {
  const { position, onClick, padding } = props;
  const selectSingletonPoi = useSelectSingletonPoi();
  const { activateTool } = useMeasureDistanceToolState();
  const { t } = useTranslation();
  const { current: map } = useMap();
  const { latitudeParam, longitudeParam } = useLatLngFromUrl();
  const { isLocked, lockedPin, recenterOnLockedPin } = useLockedMapPin();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const { showMembershipProFeatures } = useAuthState();

  const handleMeasureDistance = (lat: number, lng: number): void => {
    if (!map) {
      return;
    }
    activateTool({ lat, lng }, map);
    selectSingletonPoi({
      type: 'measureTool',
      metadata: null,
    });
    // Re-center only when measuring from the locked pin itself (the tools menu),
    // not from a context-menu / long-press point.
    const anchoredOnLockedPin =
      isLocked && lockedPin?.lat === lat && lockedPin?.lng === lng;
    if (anchoredOnLockedPin) {
      recenterOnLockedPin();
    }
  };

  const getCentroid = (): { lat: number; lng: number } => {
    // When locked, anchor on the menu's point (tools menu = locked pin, press = pressed point).
    if (isLocked) {
      return { lat: position.lat, lng: position.lng };
    }

    return {
      lat: latitudeParam ?? position.lat,
      lng: longitudeParam ?? position.lng,
    };
  };

  const linkToPaywall = pinImprovements && !showMembershipProFeatures;

  const label = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography component="span" sx={{ lineHeight: 1 }}>
        {t('map.contextMenu.measureDistanceBtnTitle')}
      </Typography>
      {pinImprovements && <UserStatusChip type="pro" size="small" />}
    </Stack>
  );

  if (linkToPaywall) {
    return (
      <MenuItem
        component={Link}
        to="/support_us?plan=pro_yearly&source=measure_distance"
        sx={{ padding }}
      >
        {label}
      </MenuItem>
    );
  }

  return (
    <MenuItem
      onClick={() => {
        const { lat, lng } = getCentroid();
        handleMeasureDistance(lat, lng);
        onClick();
      }}
      sx={{ padding }}
    >
      {label}
    </MenuItem>
  );
};
